import React, { useEffect, useRef } from 'react';

const GREEN = '#7dff8a';
const RED = '#ff6a4a';
// Campos de texto de una linea (el input tambien salta en checkbox, range, select...).
const TEXT_INPUT = /^(text|search|email|url|tel|password|number)$/;
// Lo que hay que copiar del campo para que el texto caiga igual en el div espejo.
const MIRROR = ['boxSizing', 'width', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
  'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth', 'borderStyle',
  'fontStyle', 'fontVariant', 'fontWeight', 'fontSize', 'fontFamily', 'lineHeight', 'letterSpacing',
  'wordSpacing', 'textAlign', 'textTransform', 'textIndent', 'direction'];

// Donde esta el cursor de texto en pantalla. Los input/textarea no lo dicen, asi que se copia el
// campo en un div invisible encima con un <span> justo en el cursor y se mide el span.
function caretPoint(el) {
  if (el.isContentEditable) {
    const sel = window.getSelection();
    const r = sel.rangeCount && sel.getRangeAt(0).getClientRects()[0];
    return r ? { x: r.left, y: r.top + r.height / 2 } : null;
  }
  const area = el.tagName === 'TEXTAREA';
  if (!area && !(el.tagName === 'INPUT' && TEXT_INPUT.test(el.type))) return null;
  let end = null;
  try {
    end = el.selectionEnd;
  } catch {}
  // number y email no exponen el cursor: se toma el final del texto.
  if (end == null) end = el.value.length;
  const rect = el.getBoundingClientRect();
  const cs = window.getComputedStyle(el);
  const div = document.createElement('div');
  MIRROR.forEach(prop => { div.style[prop] = cs[prop]; });
  Object.assign(div.style, {
    position: 'fixed', top: rect.top + 'px', left: rect.left + 'px', visibility: 'hidden',
    whiteSpace: area ? 'pre-wrap' : 'pre', overflowWrap: 'break-word',
  });
  const value = el.type === 'password' ? '\u2022'.repeat(el.value.length) : el.value;
  const mark = document.createElement('span');
  mark.textContent = '\u200b';
  div.append(value.slice(0, end), mark, value.slice(end));
  document.body.appendChild(div);
  const m = mark.getBoundingClientRect();
  div.remove();
  // En un input de una linea el navegador centra el texto en vertical, el div no.
  return {
    x: Math.min(rect.right, Math.max(rect.left, m.left - el.scrollLeft)),
    y: area ? m.top + m.height / 2 - el.scrollTop : rect.top + rect.height / 2,
  };
}

// Hexagonos que saltan al hacer click/tap, y que caen desde el cursor al escribir: verdes si
// eres heroe, rojos si villano, mezclados si eres neutro o no has iniciado sesion.
function ClickParticles({ faccion }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    // Por la inicial: en BD conviven 'Héroe', 'heroe', 'Villano'... con y sin tilde.
    const f = (faccion || '').toLowerCase().charAt(0);
    const color = f === 'h' ? () => GREEN
      : f === 'v' ? () => RED
        : () => (Math.random() < 0.5 ? GREEN : RED);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let parts = [];
    let raf = 0;
    let last = 0;

    const frame = (now) => {
      const dt = Math.min(50, now - last);
      last = now;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.lineWidth = 1.5;
      ctx.shadowBlur = 8;
      parts = parts.filter(p => (p.life -= dt) > 0);
      parts.forEach(p => {
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.vy += 0.0004 * dt;
        p.rot += p.vr * dt;
        ctx.globalAlpha = p.life / p.max;
        ctx.strokeStyle = ctx.shadowColor = p.color;
        ctx.beginPath();
        for (let i = 0; i < 6; i++) {
          const a = p.rot + Math.PI / 3 * i;
          ctx.lineTo(p.x + p.r * Math.cos(a), p.y + p.r * Math.sin(a));
        }
        ctx.closePath();
        ctx.stroke();
      });
      raf = parts.length ? requestAnimationFrame(frame) : 0;
    };

    const add = (x, y, vx, vy, r, life) => {
      parts.push({ x, y, vx, vy, r, rot: Math.random() * Math.PI, vr: (Math.random() - 0.5) * 0.01, life, max: life, color: color() });
    };

    const start = () => {
      if (canvas.width !== canvas.clientWidth || canvas.height !== canvas.clientHeight) {
        canvas.width = canvas.clientWidth;
        canvas.height = canvas.clientHeight;
      }
      if (!raf) {
        last = performance.now();
        raf = requestAnimationFrame(frame);
      }
    };

    const onDown = (e) => {
      for (let i = 0; i < 10; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = 0.06 + Math.random() * 0.16;
        add(e.clientX, e.clientY, Math.cos(angle) * speed, Math.sin(angle) * speed, 4 + Math.random() * 6, 500 + Math.random() * 400);
      }
      start();
    };

    // Menos y mas pequenos que en el click, con un saltito hacia arriba y luego la gravedad.
    const onInput = (e) => {
      const p = caretPoint(e.target);
      if (!p) return;
      for (let i = 0; i < 3; i++) {
        add(p.x, p.y, (Math.random() - 0.5) * 0.08, -Math.random() * 0.06, 2 + Math.random() * 3, 600 + Math.random() * 400);
      }
      start();
    };

    window.addEventListener('pointerdown', onDown);
    window.addEventListener('input', onInput);
    return () => {
      window.removeEventListener('pointerdown', onDown);
      window.removeEventListener('input', onInput);
      cancelAnimationFrame(raf);
    };
  }, [faccion]);

  return <canvas ref={canvasRef} className="clickParticles" />;
}

export default ClickParticles;
